import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import LoginRegister from './Pages/LoginRegister';
import Match from './Pages/Match';
import Profile from './Pages/Profile';
import { useAuth } from './AuthContext';
import { API_ENDPOINTS as api } from './api';
import axiosInstance from './Pages/axiosInstance';


// Enhanced error boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App error:', error, errorInfo);
    this.setState({
      error: error,
      errorInfo: errorInfo,
      retryCount: this.state.retryCount + 1
    });
  }

  handleRetry = () => {
    if (this.state.retryCount < 3) {
      this.setState({ 
        hasError: false, 
        error: null, 
        errorInfo: null 
      });
      window.location.reload();
    } else {
      // Force logout after 3 retries
      localStorage.clear();
      window.location.href = '/login';
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
            <h2 className="text-xl font-bold text-red-600 mb-4">Something went wrong</h2>
            <p className="text-gray-600 mb-4">
              We encountered an unexpected error. Please try again.
            </p>
            {this.state.retryCount < 3 ? (
              <button 
                onClick={this.handleRetry}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Try Again
              </button>
            ) : (
              <p className="text-sm text-gray-500">
                Too many retries. Please log in again.
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Loading component with progress indicator
const Loading = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50">
    <div className="text-xl font-semibold text-gray-700 bg-white p-8 rounded-lg shadow-md">
      <div className="flex items-center space-x-3">
        <div className="animate-spin h-5 w-5 text-gray-700">⌛</div>
        <span>Loading TalkConnect...</span>
      </div>
    </div>
  </div>
);

// Error component with retry functionality
const Error = ({ message, onRetry }) => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50">
    <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
      <svg className="h-12 w-12 text-red-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <h2 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h2>
      <p className="text-gray-600 mb-4">{message || 'An error occurred. Please try again.'}</p>
      {onRetry && (
        <button 
          onClick={onRetry}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
        >
          Try Again
        </button>
      )}
    </div>
  </div>
);

// Protected Route Component with enhanced validation
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  const [isValidating, setIsValidating] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const validateAccess = async () => {
      try {
        if (isLoading || !isAuthenticated) {
          setIsValidating(false);
          return;
        }

        const token = localStorage.getItem('authToken');
        if (!token) {
          throw new Error('No authentication token found');
        }

        // Verify user state
        const response = await axiosInstance.get('/api/auth/user/info');
        
        if (response.data) {
          setIsValidating(false);
          setError(null);
        } else {
          throw new Error('Failed to validate user state');
        }
      } catch (error) {
        console.error('Route validation error:', error);
        setError('Failed to validate access');
        setIsValidating(false);
        
        if (error.response?.status === 401) {
          localStorage.removeItem('authToken');
          window.location.href = '/login';
        }
      }
    };

    validateAccess();
  }, [isLoading, isAuthenticated]);

  if (isLoading || isValidating) {
    return <Loading />;
  }

  if (error) {
    return <Error message={error} />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

function App() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation(); // Add this
  const [appState, setAppState] = useState({
    isInitialized: false,
    error: null
  });

  // Add logging to debug state
  console.log('App render state:', { 
    isAuthenticated, 
    isLoading, 
    isInitialized: appState.isInitialized,
    currentPath: location.pathname 
  });

  // Initialize app
  useEffect(() => {
    const initializeApp = async () => {
      try {
        console.log('Initializing app...');
        // Clean up any existing audio contexts
        if (window.audioContext) {
          await window.audioContext.close();
        }

        // Clean up any existing socket connections
        if (window.socket) {
          window.socket.disconnect();
        }

        // Initialize audio context for iOS
        if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
          window.audioContext = new (window.AudioContext || window.webkitAudioContext)();
          await window.audioContext.resume();
        }

        setAppState({ isInitialized: true, error: null });
      } catch (error) {
        console.error('App initialization error:', error);
        setAppState({ isInitialized: false, error: 'Failed to initialize app' });
      }
    };

    initializeApp();

    // Cleanup on unmount
    return () => {
      if (window.audioContext) {
        window.audioContext.close().catch(console.error);
      }
      if (window.socket) {
        window.socket.disconnect();
      }
    };
  }, []);


useEffect(() => {
  const handleTabClose = async (event) => {
    if (isAuthenticated) {
      try {
        // Only triggers when tab/window is actually closing
        await axiosInstance.post(api.auth.setUnavailable);
      } catch (error) {
        console.error('Error updating availability on tab close:', error);
      }
    }
  };

  window.addEventListener('beforeunload', handleTabClose);
  return () => {
    window.removeEventListener('beforeunload', handleTabClose);
  };
}, [isAuthenticated]);

   // Modified loading condition
   const shouldShowLoading = (isLoading || !appState.isInitialized) && 
   location.pathname !== '/login';

if (shouldShowLoading) {
console.log('Showing loading screen');
return <Loading />;
}

// Show error state
if (appState.error) {
return <Error message={appState.error} onRetry={() => window.location.reload()} />;
}
  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gray-50">
        <Routes>
          {/* Public Routes */}
          <Route 
            path="/login" 
            element={
              isAuthenticated ? 
                <Navigate to="/" replace /> : 
                <LoginRegister />
            } 
          />

          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Match />
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />

          {/* Error Routes */}
          <Route 
            path="/error" 
            element={<Error />} 
          />

          {/* Catch all route */}
          <Route 
            path="*" 
            element={
              <Navigate 
                to={isAuthenticated ? "/" : "/login"} 
                replace 
              />
            } 
          />
        </Routes>

        {/* Network Status Indicator */}
        <NetworkStatus />

        {/* Error Alert */}
        <ErrorAlert />
      </div>
    </ErrorBoundary>
  );
}

// Network status monitoring component
const NetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showIndicator, setShowIndicator] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowIndicator(true);
      setTimeout(() => setShowIndicator(false), 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowIndicator(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!showIndicator) return null;

  return (
    <div className={`fixed bottom-4 left-4 p-4 rounded-lg shadow-lg transition-all duration-300 ${
      isOnline ? 'bg-green-500' : 'bg-red-500'
    }`}>
      <div className="flex items-center space-x-2 text-white">
        <div className={`w-2 h-2 rounded-full ${
          isOnline ? 'bg-green-200' : 'bg-red-200'
        } animate-pulse`} />
        <span>
          {isOnline ? 'Connection restored' : 'No internet connection'}
        </span>
      </div>
    </div>
  );
};

// Global error alert component
const ErrorAlert = () => {
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    const handleError = (event) => {
      if (event.error instanceof Error) {
        setErrors(prev => [...prev, {
          id: Date.now(),
          message: event.error.message,
          timestamp: new Date().toISOString()
        }]);
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', (event) => {
      handleError({ error: event.reason });
    });

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleError);
    };
  }, []);

  const removeError = (errorId) => {
    setErrors(prev => prev.filter(error => error.id !== errorId));
  };

  return (
    <div className="fixed top-4 right-4 space-y-2 z-50">
      {errors.map(error => (
        <div
          key={error.id}
          className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-lg max-w-md"
          role="alert"
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="font-bold">Error</p>
              <p>{error.message}</p>
            </div>
            <button
              onClick={() => removeError(error.id)}
              className="ml-4 text-red-500 hover:text-red-700"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

// Performance monitoring
const reportWebVitalsCallback = (metric) => {
  try {
    if (process.env.NODE_ENV === 'development') {
      console.log(metric);
    }
    // You can send metrics to your analytics service here
  } catch (error) {
    console.error('Error reporting web vitals:', error);
  }
};

// Add cleanup on page unload
window.onbeforeunload = function() {
  try {
    // Clean up any active connections
    const socket = window.socket;
    if (socket) {
      socket.disconnect();
    }

    // Clean up audio context if it exists
    const audioContext = window.audioContext;
    if (audioContext) {
      audioContext.close();
    }
  } catch (error) {
    console.error('Cleanup error:', error);
  }
};

// Mobile device detection
const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
if (isMobile) {
  document.body.classList.add('mobile-device');
}

// Add dark mode detection and handling
if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
  document.body.classList.add('dark-theme');
}

// Listen for theme changes
window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', e => {
  if (e.matches) {
    document.body.classList.add('dark-theme');
  } else {
    document.body.classList.remove('dark-theme');
  }
});

// Add viewport height fix for mobile browsers
function setMobileViewportHeight() {
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
}

window.addEventListener('resize', setMobileViewportHeight);
setMobileViewportHeight();

export default App;